import { useState } from "react";
import { Permission } from "../../../model/permission";
import { RolPermission } from "../rol.Dto";
import { patchRol } from "../../../resources/helper/rol";
import { useDispatch } from "react-redux";
import { typeAlert } from "@affinitysystemsgroup/github-packages-affinity-components/dist";

interface DraggableItem {
  id: string;
  name: string;
  description: string;
}

export interface AssignedPermissionsList {
  id: string;
  name: string;
  description: string;
}
export const useRolManagement = (
  setTrigger: (trigger: boolean) => void,
  setSeeToast: (seeToast: {
    show: boolean;
    type: typeAlert;
    message: string;
  }) => void
) => {
  const [loadingSaveRolPermissions, setLoadingSaveRolPermissions] =
    useState<boolean>(false);
  const [openDrawerRolList, setOpenDrawerRolList] = useState<boolean>(false);
  const [openDrawerEdit, setOpenDrawerEdit] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);

  const [assignedQuery, setAssignedQuery] = useState<string>("");
  const [availableQuery, setAvailableQuery] = useState<string>("");

  const [assignedPermissionsToEdit, setAssignedPermissionsToEdit] = useState<
    DraggableItem[]
  >([]);
  const [
    assignedPermissionsToEditFiltered,
    setAssignedPermissionsToEditFiltered,
  ] = useState<DraggableItem[]>([]);

  const [permissionAvailableToAssign, setPermissionAvailableToAssign] =
    useState<DraggableItem[]>([]);
  const [
    permissionAvailableToAssignFiltered,
    setPermissionAvailableToAssignFiltered,
  ] = useState<DraggableItem[]>([]);
  const [selectedRol, setSelectedRol] = useState<RolPermission | null>(null);
  const dispatch = useDispatch();

  const handleOpenDrawerRolList = (item: RolPermission) => {
    setSelectedRol(item);
    const permissions =
      item.permission?.map((permission) => ({
        id: permission.id || "",
        name: permission.name || "",
        description: permission.description || "",
      })) || [];
    setAssignedPermissionsToEdit(permissions);
    setAssignedPermissionsToEditFiltered(permissions);
    setOpenDrawerRolList(true);
  };

  const handleOpenDrawerEdit = (item: RolPermission) => {
    setOpenDrawerEdit(true);
    setSelectedRol(item);
    const permissions =
      item.permission?.map((permission) => ({
        id: permission.id || "",
        name: permission.name || "",
        description: permission.description || "",
      })) || [];
    setAssignedPermissionsToEdit(permissions);
    setAssignedPermissionsToEditFiltered(permissions);
  };

  const onSearchAvailableToAssign = (value: string) => {
    setAvailableQuery(value);
    setPermissionAvailableToAssignFiltered(
      permissionAvailableToAssign.filter((permission) =>
        permission.name.toLowerCase().includes(value.toLowerCase())
      )
    );
  };

  const onSearchAssignedToEdit = (value: string) => {
    setAssignedQuery(value);
    setAssignedPermissionsToEditFiltered(
      assignedPermissionsToEdit.filter((permission) =>
        permission.name.toLowerCase().includes(value.toLowerCase())
      )
    );
  };

  const handlePermissionAvailableToAssign = (permissions: Permission[]) => {
    const permissionsFiltered = permissions.map((permission) => ({
      id: permission.id || "",
      name: permission.name || "",
      description: permission.description || "",
    }));
    setPermissionAvailableToAssign(permissionsFiltered);
    setPermissionAvailableToAssignFiltered(permissionsFiltered);
  };
  const onRoleMove = (
    itemId: string,
    from: "available" | "assigned",
    to: "available" | "assigned"
  ) => {
    if (from === "available" && to === "assigned") {
      const itemToMove = permissionAvailableToAssign.find(
        (permission) => permission.id === itemId
      );
      if (itemToMove) {
        // Update assigned permissions
        const newAssignedPermissions = [
          ...assignedPermissionsToEdit,
          itemToMove,
        ];
        setAssignedPermissionsToEdit(newAssignedPermissions);
        setAssignedPermissionsToEditFiltered(
          newAssignedPermissions.filter((permission) =>
            permission.name.toLowerCase().includes(assignedQuery.toLowerCase())
          )
        );

        // Update available permissions
        const newAvailablePermissions = permissionAvailableToAssign.filter(
          (permission) => permission.id !== itemId
        );
        setPermissionAvailableToAssign(newAvailablePermissions);
        setPermissionAvailableToAssignFiltered(
          newAvailablePermissions.filter((permission) =>
            permission.name.toLowerCase().includes(availableQuery.toLowerCase())
          )
        );
      }
    } else if (from === "assigned" && to === "available") {
      const itemToMove = assignedPermissionsToEdit.find(
        (permission) => permission.id === itemId
      );
      if (itemToMove) {
        // Update assigned permissions
        const newAssignedPermissions = assignedPermissionsToEdit.filter(
          (permission) => permission.id !== itemId
        );
        setAssignedPermissionsToEdit(newAssignedPermissions);
        setAssignedPermissionsToEditFiltered(
          newAssignedPermissions.filter((permission) =>
            permission.name.toLowerCase().includes(assignedQuery.toLowerCase())
          )
        );

        // Update available permissions
        const newAvailablePermissions = [
          ...permissionAvailableToAssign,
          itemToMove,
        ];
        setPermissionAvailableToAssign(newAvailablePermissions);
        setPermissionAvailableToAssignFiltered(
          newAvailablePermissions.filter((permission) =>
            permission.name.toLowerCase().includes(availableQuery.toLowerCase())
          )
        );
      }
    }
  };

  const onRoleDelete = (itemId: string) => {
    const itemToMove = assignedPermissionsToEdit.find(
      (permission) => permission.id === itemId
    );
    if (itemToMove) {
      // Update assigned permissions
      const newAssignedPermissions = assignedPermissionsToEdit.filter(
        (permission) => permission.id !== itemId
      );
      setAssignedPermissionsToEdit(newAssignedPermissions);
      setAssignedPermissionsToEditFiltered(
        newAssignedPermissions.filter((permission) =>
          permission.name.toLowerCase().includes(assignedQuery.toLowerCase())
        )
      );

      // Update available permissions
      const newAvailablePermissions = [
        ...permissionAvailableToAssign,
        itemToMove,
      ];
      setPermissionAvailableToAssign(newAvailablePermissions);
      setPermissionAvailableToAssignFiltered(
        newAvailablePermissions.filter((permission) =>
          permission.name.toLowerCase().includes(availableQuery.toLowerCase())
        )
      );
    }
  };

  const onSaveRolPermissions = async (permissions: Permission[]) => {
    const body = {
      id: selectedRol?.id,
      name: selectedRol?.code,
      description: selectedRol?.type,
      permissions: permissions.map((permission) => permission.id),
    };
    try {
      setLoadingSaveRolPermissions(true);
      await patchRol(body, selectedRol?.id || "");
      setTrigger(true);
      setSeeToast({
        show: true,
        type: typeAlert.success,
        message: "Rol actualizado correctamente",
      });
    } catch (error) {
      setSeeToast({
        show: true,
        type: typeAlert.error,
        message: "Error al actualizar el rol",
      });
    } finally {
      setLoadingSaveRolPermissions(false);
    }
  };

  return {
    loadingSaveRolPermissions,
    openDrawerRolList,
    setOpenDrawerRolList,
    openDrawerEdit,
    setOpenDrawerEdit,
    permissions,
    setPermissions,
    assignedPermissionsToEdit,
    selectedRol,
    setSelectedRol,
    handleOpenDrawerRolList,
    handleOpenDrawerEdit,
    handlePermissionAvailableToAssign,
    permissionAvailableToAssign,
    permissionAvailableToAssignFiltered,
    assignedPermissionsToEditFiltered,
    onSearchAvailableToAssign,
    onSearchAssignedToEdit,
    assignedQuery,
    availableQuery,
    onRoleMove,
    onRoleDelete,
    onSaveRolPermissions,
  };
};
